import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';
import { ReactComponent as BaseInfoIcon } from '@ubeya/shared-web/assets/info.svg';
import { Modal as BaseModal, FlexColumn, Header2SemiBold } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useOSEmployee from '@ubeya/shared/hooks/employees/useOSEmployee';
import useEmployee from '@ubeya/shared/hooks/employees/useEmployee';
import { EMPLOYEE_CLIENT_PREFERENCE_STATUS } from '@ubeya/shared-web/utils/employee';
import { uniq } from 'lodash';

const Modal = styled(BaseModal)`
  .content {
    display: flex;
    align-items: center;
  }
`;

const InfoIcon = styled(BaseInfoIcon)`
  margin-bottom: 10px;
  g {
    circle {
      stroke: ${({ theme }) => theme.colors.primary};
    }
    path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const Content = styled(FlexColumn)`
  gap: 8px;
  padding-block: 32px;
  width: 80%;
  align-items: center;
  text-align: center;
`;

const Title = styled(Header2SemiBold)`
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
`;

// isClient - indicates if Client Account or Supplier Account
const MarkPreferredOnRatingModal = ({ data, onClose, onEmployeeStatusChange, isClient = true }) => {
  const { t } = useContext(ConfigContext);
  const { account } = useAccount();
  const { updateEmployee: updateOSEmployee } = useOSEmployee({ employeeId: data?.employee?.id, enabled: false });
  const { updateEmployee, isLoading, data: employeeData } = useEmployee({
    employeeId: data?.employee?.id,
    enabled: !isClient
  });

  const employeeName = `${data?.employee?.firstName} ${data?.employee?.lastName}`.trim();
  const clientName = isClient ? account.name : data?.clientBranch?.clientName ?? data?.clientName;

  const onMarkPreferred = useCallback(async () => {
    if (!isClient) {
      const newPreferredClient = data.clientId ?? data?.clientBranch?.accountId;

      const blockedClients =
        employeeData?.employeeClients
          ?.filter((client) => client.status === EMPLOYEE_CLIENT_PREFERENCE_STATUS.BLOCKED)
          .map((client) => client.clientId) || [];

      const preferredClients =
        employeeData?.employeeClients
          ?.filter((client) => client.status === EMPLOYEE_CLIENT_PREFERENCE_STATUS.PREFERRED)
          .map((client) => client.clientId) || [];

      await updateEmployee({
        accountId: account.id,
        employeeId: data.employee.id,
        preferredClients: uniq([...preferredClients, newPreferredClient]),
        blockedClients: blockedClients.filter((client) => client !== newPreferredClient)
      });
    } else {
      await updateOSEmployee({
        accountId: account.id,
        employeeId: data.employee.id ?? data.employee.employeeId,
        clientStatus: EMPLOYEE_CLIENT_PREFERENCE_STATUS.PREFERRED
      });
    }
    onEmployeeStatusChange?.();
    onClose();
  }, [
    account.id,
    data?.clientBranch?.accountId,
    data.clientId,
    data.employee.employeeId,
    data.employee.id,
    isClient,
    onClose,
    onEmployeeStatusChange,
    updateEmployee,
    employeeData,
    updateOSEmployee
  ]);

  return (
    <>
      <Modal
        size="small"
        onConfirm={onMarkPreferred}
        onClose={onClose}
        isPaddingContent={false}
        showConfirmButton
        showCancelButton
        loadingConfirm={isLoading}
        confirmText={t('markAsPreferred')}
        usePortal>
        <Content>
          <InfoIcon />
          <Title>{t('employeeRatedFiveStarModalHeader')}</Title>
          <p>{t('employeeRatedFiveStarModalBody', { employeeName, clientName })}</p>
        </Content>
      </Modal>
    </>
  );
};

export default MarkPreferredOnRatingModal;
